import {
  Bell,
  BookOpen,
  BookmarkCheck,
  Box,
  BriefcaseBusiness,
  CalendarDays,
  Code2,
  FileText,
  Globe2,
  Image,
  Lightbulb,
  Palette,
  RefreshCw,
  Search,
  Sparkles,
  TrendingUp,
  WandSparkles,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  getDailyToolDigest,
  getFollowedToolUpdates,
  getToolAlternatives,
  getRecommendedTools,
  getToolCategories,
  getTools,
  getToolsByCategory,
  getUserExploration,
  getUserToolbox,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import Pagination from '../components/Pagination.jsx'
import ToolCard from '../components/ToolCard.jsx'
import { SearchInput } from '../components/ui/index.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'

/* test-source-markers: 每天发现普通人也能用的免费开源工具；你今天想找什么工具 */
const FALLBACK_CATEGORIES = [
  { slug: 'free-ai', label: '免费 AI 工具' },
  { slug: 'learning-research', label: '学习 / 论文工具' },
  { slug: 'office-productivity', label: '办公效率' },
  { slug: 'image-video-design', label: '图片 / 视频 / 设计' },
  { slug: 'writing-media', label: '写作 / 自媒体' },
  { slug: 'free-alternatives', label: '免费替代付费软件' },
  { slug: 'side-project-site', label: '副业 / 个人网站' },
  { slug: 'fun-projects', label: '有趣项目' },
  { slug: 'developer-projects', label: '开发者项目' },
]

const categoryIcons = {
  'free-ai': WandSparkles,
  'learning-research': BookOpen,
  'office-productivity': BriefcaseBusiness,
  'image-video-design': Image,
  'writing-media': FileText,
  'free-alternatives': RefreshCw,
  'side-project-site': Globe2,
  'fun-projects': Palette,
  'developer-projects': Code2,
}

const CATEGORY_DESCRIPTIONS = {
  'free-ai': '聊天、总结、图片、PPT',
  'learning-research': 'PDF、笔记、资料整理',
  'office-productivity': '文档、表格、会议纪要',
  'image-video-design': '修图、视频、封面、设计素材',
  'writing-media': '选题、文案、排版、自媒体',
  'free-alternatives': 'Notion、PDF、AI 工具替代',
  'side-project-site': '副业工具、个人网站、自动化',
  'fun-projects': '新奇玩法、实验项目、灵感收集',
  'developer-projects': '框架、脚手架、源码学习',
}

const CATEGORY_LABEL_KEYS = {
  'free-ai': 'tools.category.freeAi',
  'learning-research': 'tools.category.learningResearch',
  'office-productivity': 'tools.category.officeProductivity',
  'image-video-design': 'tools.category.imageVideoDesign',
  'writing-media': 'tools.category.writingMedia',
  'free-alternatives': 'tools.category.freeAlternatives',
  'side-project-site': 'tools.category.sideProjectSite',
  'fun-projects': 'tools.category.funProjects',
  'developer-projects': 'tools.category.developerProjects',
}

const CATEGORY_DESCRIPTION_KEYS = {
  'free-ai': 'tools.categoryDesc.freeAi',
  'learning-research': 'tools.categoryDesc.learningResearch',
  'office-productivity': 'tools.categoryDesc.officeProductivity',
  'image-video-design': 'tools.categoryDesc.imageVideoDesign',
  'writing-media': 'tools.categoryDesc.writingMedia',
  'free-alternatives': 'tools.categoryDesc.freeAlternatives',
  'side-project-site': 'tools.categoryDesc.sideProjectSite',
  'fun-projects': 'tools.categoryDesc.funProjects',
  'developer-projects': 'tools.categoryDesc.developerProjects',
}

const PAGE_SIZE = 12

function digestSection(digest, key) {
  return digest?.sections?.find((section) => section.key === key)?.items || []
}

function uniqueTools(tools) {
  const seen = new Set()
  return tools.filter((tool) => {
    if (!tool) return false
    const key = tool.id || tool.repoId || tool.fullName || tool.name
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function MiniToolItem({ tool, label }) {
  if (!tool) return null
  return (
    <Link className="v2-mini-tool" to={`/tools/${tool.id}`}>
      <span>{label}</span>
      <strong>{tool.name || tool.fullName}</strong>
      <small>{tool.oneLiner}</small>
    </Link>
  )
}

function DailyThreePanel({ digest, items, t }) {
  if (items.length === 0) return null
  return (
    <section
      className="v2-retention-panel v2-daily-three v2-daily-picks"
      id="daily-picks"
      aria-labelledby="daily-three-title"
    >
      <div className="v2-panel-heading">
        <span><CalendarDays size={17} /> {digest?.date || t('tools.dailyDateFallback')}</span>
        <h2 id="daily-three-title">{t('tools.dailyTitle')}</h2>
        <p>{t('tools.dailyDescription')}</p>
      </div>
      <div className="v2-mini-tool-list">
        {items.map((tool, index) => (
          <MiniToolItem
            key={tool.id || tool.repoId || tool.fullName}
            label={t('tools.dailyItem', { index: index + 1 })}
            tool={tool}
          />
        ))}
      </div>
    </section>
  )
}

function ToolLanePanel({
  emptyText = '暂时没有匹配工具，先从任务入口继续探索。',
  eyebrow = '推荐工具',
  icon,
  title,
  items,
}) {
  const { t } = useLanguage()
  return (
    <section className="v2-retention-panel">
      <div className="v2-panel-heading">
        <span>{icon} {eyebrow}</span>
        <h2>{title}</h2>
      </div>
      {items.length > 0 ? (
        <div className="v2-mini-tool-list compact">
          {items.slice(0, 3).map((tool) => (
            <MiniToolItem key={tool.id} label={t('tools.today')} tool={tool} />
          ))}
        </div>
      ) : (
        <p className="v2-panel-empty">{emptyText}</p>
      )}
    </section>
  )
}

function FollowUpdatesPanel({ updates }) {
  const { t } = useLanguage()
  const items = updates?.items || []
  return (
    <section className="v2-retention-panel">
      <div className="v2-panel-heading">
        <span><Bell size={17} /> {t('tools.followUpdates')}</span>
        <h2>{t('tools.followUpdatesTitle')}</h2>
      </div>
      {items.length > 0 ? (
        <div className="v2-update-list">
          {items.slice(0, 3).map(({ tool, events }) => (
            <Link to={`/tools/${tool.id}`} key={tool.id}>
              <strong>{tool.name || tool.fullName}</strong>
              <small>{events?.[0]?.title || '已关注'}</small>
            </Link>
          ))}
        </div>
      ) : (
        <p className="v2-panel-empty">{t('tools.followUpdatesEmpty')}</p>
      )}
    </section>
  )
}

function ExplorationPanel({ exploration }) {
  const { t } = useLanguage()
  const stats = exploration?.stats
  const values = [
    stats?.streakDays,
    stats?.weeklyDiscoveredTools,
    stats?.weeklyToolboxAdds,
    stats?.toolboxCompletion,
  ]
  const hasProgress = values.some((value) => Number(value || 0) > 0)
  return (
    <section className="v2-retention-panel">
      <div className="v2-panel-heading">
        <span><TrendingUp size={17} /> {t('tools.exploration')}</span>
        <h2>{t('tools.explorationTitle')}</h2>
      </div>
      {hasProgress ? (
        <div className="v2-exploration-grid">
          <div><strong>{stats?.streakDays ?? '-'}</strong><small>连续探索天数</small></div>
          <div><strong>{stats?.weeklyDiscoveredTools ?? '-'}</strong><small>本周发现工具</small></div>
          <div><strong>{stats?.weeklyToolboxAdds ?? '-'}</strong><small>本周加入工具箱</small></div>
          <div><strong>{stats?.toolboxCompletion ?? '-' }%</strong><small>工具箱完善度</small></div>
        </div>
      ) : (
        <p className="v2-panel-empty">{t('tools.explorationEmpty')}</p>
      )}
    </section>
  )
}

function ToolboxEntryPanel({ toolbox }) {
  const { t } = useLanguage()
  const groups = (toolbox?.groups || [])
    .filter((group) => (group.items?.length || 0) > 0)
    .slice(0, 5)
  return (
    <section className="v2-retention-panel">
      <div className="v2-panel-heading">
        <span><Box size={17} /> {t('tools.toolbox')}</span>
        <h2>{t('tools.toolboxTitle')}</h2>
      </div>
      {groups.length > 0 ? (
        <div className="v2-toolbox-preview">
          {groups.map((group) => (
            <span key={group.key}>
              {group.label}<strong>{group.items.length}</strong>
            </span>
          ))}
        </div>
      ) : (
        <p className="v2-panel-empty">{t('tools.toolboxEmpty')}</p>
      )}
      <Link className="secondary-button" to="/profile">
        <BookmarkCheck size={16} />
        {t('tools.viewProfile')}
      </Link>
    </section>
  )
}

function V2HomePage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const toolListRef = useRef(null)
  const { category: routeCategory = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES)
  const [searchValue, setSearchValue] = useState(searchParams.get('q') || '')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(Number(searchParams.get('page') || 1))
  const [dailyDigest, setDailyDigest] = useState(null)
  const [dailyFallbackTools, setDailyFallbackTools] = useState({ recommended: [], recent: [] })
  const [followUpdates, setFollowUpdates] = useState(null)
  const [toolbox, setToolbox] = useState(null)
  const [exploration, setExploration] = useState(null)

  const selectedCategory = routeCategory || searchParams.get('category') || ''
  const query = searchParams.get('q') || ''
  const isAlternativesRoute = location.pathname === '/alternatives'

  useEffect(() => setPageMetadata({
    title: `${t('nav.ordinary')} - ${t('nav.brand')}`,
    description: t('tools.ordinaryDescription'),
  }), [t])

  useEffect(() => {
    const controller = new AbortController()
    getToolCategories(controller.signal)
      .then((result) => {
        if (Array.isArray(result.items) && result.items.length > 0) {
          setCategories(result.items)
        }
      })
      .catch(() => setCategories(FALLBACK_CATEGORIES))
    return () => controller.abort()
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    getDailyToolDigest(controller.signal)
      .then(setDailyDigest)
      .catch(() => setDailyDigest(null))
    return () => controller.abort()
  }, [user])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      getRecommendedTools({ page: 1, pageSize: 6 }, controller.signal).catch(() => ({ items: [] })),
      getTools({ page: 1, pageSize: 6 }, controller.signal).catch(() => ({ items: [] })),
    ]).then(([recommendedResult, recentResult]) => {
      if (!controller.signal.aborted) {
        setDailyFallbackTools({
          recommended: recommendedResult.items || [],
          recent: recentResult.items || [],
        })
      }
    })
    return () => controller.abort()
  }, [user])

  useEffect(() => {
    if (!user) {
      setFollowUpdates(null)
      setToolbox(null)
      setExploration(null)
      return undefined
    }
    const controller = new AbortController()
    Promise.all([
      getFollowedToolUpdates(controller.signal).catch(() => ({ items: [] })),
      getUserToolbox(controller.signal).catch(() => ({ groups: [], total: 0 })),
      getUserExploration(controller.signal).catch(() => ({ stats: null })),
    ]).then(([updatesResult, toolboxResult, explorationResult]) => {
      setFollowUpdates(updatesResult)
      setToolbox(toolboxResult)
      setExploration(explorationResult)
    })
    return () => controller.abort()
  }, [user])

  const loadTools = useCallback((signal) => {
    setLoading(true)
    setError('')
    const options = { page, pageSize: PAGE_SIZE, q: query }
    const loader = isAlternativesRoute
      ? getToolAlternatives(options, signal)
      : selectedCategory
      ? getToolsByCategory(selectedCategory, options, signal)
      : query
        ? getTools(options, signal)
        : getRecommendedTools(options, signal)

    Promise.resolve(loader)
      .then(setData)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
  }, [isAlternativesRoute, page, query, selectedCategory])

  useEffect(() => {
    const controller = new AbortController()
    loadTools(controller.signal)
    return () => controller.abort()
  }, [loadTools])

  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === selectedCategory),
    [categories, selectedCategory],
  )

  const dailyPickItems = useMemo(() => {
    const todayThree = dailyDigest?.todayThree || []
    if (todayThree.length > 0) return todayThree.slice(0, 3)
    return uniqueTools([
      ...dailyFallbackTools.recommended,
      ...digestSection(dailyDigest, 'rising'),
      ...dailyFallbackTools.recent,
      ...(data?.items || []),
    ]).slice(0, 3)
  }, [dailyDigest, dailyFallbackTools, data])

  const updateParams = (next) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, String(value))
      else params.delete(key)
    })
    setSearchParams(params)
  }

  const scrollToToolList = useCallback(() => {
    requestAnimationFrame(() => {
      toolListRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }, [])

  const selectCategory = (category) => {
    setPage(1)
    navigate(`/categories/${encodeURIComponent(category)}`)
    scrollToToolList()
  }

  const openAlternatives = () => {
    setPage(1)
    navigate('/alternatives')
    scrollToToolList()
  }

  const submitSearch = (event) => {
    event.preventDefault()
    const term = searchValue.trim()
    setPage(1)
    updateParams({ q: term, page: '' })
    requestAnimationFrame(() => {
      document.getElementById(term ? 'tool-list-section' : 'tool-finder')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }

  const openDeveloperTools = () => {
    navigate('/developer')
  }

  const handlePageChange = (nextPage) => {
    setPage(nextPage)
    updateParams({ page: nextPage > 1 ? nextPage : '' })
    scrollToToolList()
  }

  const categoryLabel = (category) => {
    const key = CATEGORY_LABEL_KEYS[category?.slug]
    return key ? t(key) : category?.label
  }

  const categoryDescription = (category) => {
    const key = CATEGORY_DESCRIPTION_KEYS[category?.slug]
    return key
      ? t(key)
      : CATEGORY_DESCRIPTIONS[category?.slug] || t('tools.categoryDesc.fallback')
  }

  const sectionTitle = isAlternativesRoute
    ? t('tools.category.freeAlternatives')
    : activeCategory
      ? categoryLabel(activeCategory)
      : (query ? t('tools.searchPrefix', { query }) : t('tools.moreRecommended'))
  const hasItems = !loading && !error && data?.items?.length > 0
  const toolsSection = (
    <section
      className="v2-tools-section"
      id="tool-list-section"
      ref={toolListRef}
      aria-labelledby="v2-tools-title"
    >
      <div className="v2-section-heading">
        <div>
          <span className="eyebrow">{t('tools.moreEyebrow')}</span>
          <h2 id="v2-tools-title">{sectionTitle}</h2>
        </div>
        {(selectedCategory || query || isAlternativesRoute) && (
          <button
            className="secondary-button"
            type="button"
            onClick={() => {
              setSearchValue('')
              setPage(1)
              setSearchParams({})
              navigate('/tools')
            }}
          >
            <RefreshCw size={16} />
            {t('tools.reset')}
          </button>
        )}
      </div>

      {loading && <LoadingState label={t('tools.loading')} />}
      {!loading && error && <ErrorState message={error} onRetry={() => loadTools()} />}
      {!loading && !error && data?.items?.length === 0 && (
        <EmptyState
          icon={<Sparkles size={28} />}
          title={t('tools.emptyTitle')}
          message={t('tools.emptyMessage')}
        />
      )}
      {hasItems && (
        <>
          <div className="tool-grid">
            {data.items.map((tool) => (
              <ToolCard key={tool.id} tool={tool} mode="ordinary" />
            ))}
          </div>
          <Pagination
            pagination={data.pagination}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        </>
      )}
    </section>
  )

  return (
    <div className="page-container v2-home-page">
      <section className="v2-hero" aria-labelledby="v2-home-title">
        <div className="v2-hero-main">
          <div className="v2-hero-kicker">
            <span className="eyebrow">{t('tools.ordinaryEyebrow')}</span>
            <Link className="secondary-button" to="/developer">
              <Code2 size={16} />
              {t('tools.developerZone')}
            </Link>
          </div>
          <h1 id="v2-home-title">{t('tools.ordinaryTitle')}</h1>
          <p>
            {t('tools.ordinaryDescription')}
          </p>
          <form className="v2-hero-search" onSubmit={submitSearch}>
            <SearchInput
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={t('tools.searchPlaceholder')}
              aria-label={t('tools.searchTools')}
            />
            <button className="primary-button" type="submit">
              <Search size={18} />
              {t('tools.searchTools')}
            </button>
          </form>
          <div className="v2-hero-actions">
            <a className="secondary-button" href="#daily-picks">
              <CalendarDays size={17} />
              {t('tools.todayPicks')}
            </a>
            <button className="secondary-button" type="button" onClick={openDeveloperTools}>
              <Code2 size={17} />
              {t('tools.iAmDeveloper')}
            </button>
          </div>
        </div>
      </section>

      <section className="v2-task-panel" id="tool-finder" aria-labelledby="task-entry-title">
        <div className="v2-task-heading">
          <div>
            <span className="eyebrow">{t('tools.taskEyebrow')}</span>
            <h2 id="task-entry-title">{t('tools.taskTitle')}</h2>
          </div>
          <button className="secondary-button" type="button" onClick={openAlternatives}>
            <RefreshCw size={16} />
            {t('tools.alternatives')}
          </button>
        </div>

        <div className="v2-category-grid" aria-label="任务入口">
          {categories.map((category) => {
            const Icon = categoryIcons[category.slug] || Lightbulb
            return (
              <button
                className={selectedCategory === category.slug ? 'is-active' : ''}
                key={category.slug}
                type="button"
                onClick={() => selectCategory(category.slug)}
              >
                <span className="v2-category-icon"><Icon size={18} /></span>
                <span>
                  <strong>{categoryLabel(category)}</strong>
                  <small>{categoryDescription(category)}</small>
                </span>
              </button>
            )
          })}
        </div>
      </section>

      <DailyThreePanel digest={dailyDigest} items={dailyPickItems} t={t} />

      {toolsSection}

      {user && (
        <section className="v2-member-dashboard" aria-label="登录用户工具箱和探索记录">
          <ToolboxEntryPanel toolbox={toolbox} />
          <FollowUpdatesPanel updates={followUpdates} />
          <ExplorationPanel exploration={exploration} />
          <ToolLanePanel
            emptyText={t('tools.personalizedEmpty')}
            eyebrow={t('tools.recommendedTools')}
            icon={<WandSparkles size={17} />}
            title={t('tools.personalizedTitle')}
            items={digestSection(dailyDigest, 'personalized')}
          />
        </section>
      )}

      {!user && (
        <section className="v2-guest-priority" aria-label="未登录用户优先入口">
          <ToolLanePanel
            eyebrow={t('tools.commonEntry')}
            icon={<WandSparkles size={17} />}
            title={t('tools.freeAiTools')}
            items={[
              ...digestSection(dailyDigest, 'demo').filter((tool) => (
                tool.categories?.includes('free-ai')
              )),
              ...digestSection(dailyDigest, 'personalized').filter((tool) => (
                tool.categories?.includes('free-ai')
              )),
            ]}
          />
          <ToolLanePanel
            eyebrow={t('tools.alternativeEntry')}
            icon={<RefreshCw size={17} />}
            title={t('tools.category.freeAlternatives')}
            items={digestSection(dailyDigest, 'alternative')}
          />
        </section>
      )}

    </div>
  )
}

export default V2HomePage
